/**
 * 客户邮箱管理模态框
 *
 * 以模态框形式管理单个客户的所有邮箱地址。支持增删改查、设置主邮箱、
 * 查看邮箱验证状态（有效/无效/风险/待验证）。支持分页浏览和键盘交互。
 *
 * 自定义元素：`crm-customer-emails-modal`
 *
 * 使用方式：
 * - 父组件通过 `show(customerId, customerName)` 方法打开模态框
 * - 组件内部通过 `customerApi` 与后端交互
 * - 关闭时调用 `close()` 方法，会重置所有状态
 *
 * 主要功能：
 * - 添加新邮箱地址（带格式校验和去重）
 * - 编辑现有邮箱地址（行内编辑）
 * - 删除邮箱地址（带确认弹窗）
 * - 设置主邮箱
 * - 查看邮箱验证状态（valid/invalid/risky/pending）
 * - 分页浏览邮箱列表
 * - ESC 键关闭模态框
 *
 * 依赖模块：
 * - `customerApi`：客户邮箱 API
 * - `crm-loading-overlay`：加载遮罩
 * - `crm-pagination`：分页组件
 * - `showConfirm`：确认弹窗
 * - `toast`：消息提示
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import type { CustomerEmail } from '../types.js';
import { toast } from '../components/toast.js';
import { customerApi } from '../api.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/pagination.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';

@customElement('crm-customer-emails-modal')
export class CrmCustomerEmailsModal extends BaseElement {
  static styles = css``;

  /** 当前管理的客户 ID（0 表示未选中客户） */
  @state()
  private customerId = 0;

  /** 当前管理的客户名称（用于模态框标题显示） */
  @state()
  private customerName = '';

  /** 邮箱列表数据 */
  @state()
  private emails: CustomerEmail[] = [];

  /** 加载状态标志 */
  @state()
  private isLoading = false;

  /** 新邮箱输入值 */
  @state()
  private newEmail = '';

  /** 邮箱输入的错误提示信息（如格式不正确、重复等） */
  @state()
  private emailInputError = '';

  /** 当前正在编辑的邮箱 ID（null 表示无编辑状态） */
  @state()
  private editingId: number | null = null;

  /** 编辑模式下的邮箱输入值 */
  @state()
  private editEmail = '';

  /** 邮箱总数（用于分页） */
  @state()
  private total = 0;

  /** 当前页码（从 1 开始） */
  @state()
  private currentPage = 1;

  /** 每页显示数 */
  @state()
  private perPage = 20;

  /** 模态框是否可见 */
  @state()
  private visible = false;
  /**
   * 遮罩点击追踪标志
   * 用于区分点击的是遮罩背景还是模态框内容：
   * mousedown 记录是否点在遮罩上，click 时据此判断是否关闭。
   */
  private _backdropMousedown = false;
  /** 邮箱列表请求序号，仅允许最后一次客户或分页请求更新弹窗。 */
  private _listRequestId = 0;

  /**
   * ESC 键处理器：模态框可见时按 ESC 关闭
   * 使用箭头函数绑定 this，确保在事件监听器中正确引用。
   */
  private handleEscKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  /**
   * 组件挂载时触发，注册 ESC 键监听器
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleEscKey);
  }

  /**
   * 组件卸载时触发，移除 ESC 键监听器防止内存泄漏
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.handleEscKey);
  }

  /**
   * 打开模态框并加载指定客户的邮箱列表
   *
   * @param customerId - 客户 ID
   * @param customerName - 客户名称（用于标题显示）
   */
  show(customerId: number, customerName: string) {
    this.customerId = customerId;
    this.customerName = customerName;
    this.currentPage = 1;
    this.visible = true;
    this.loadEmails();
  }

  /**
   * 关闭模态框并重置所有状态
   *
   * 清空客户信息、邮箱列表、编辑状态和输入值，
   * 确保下次打开时是干净的初始状态。
   */
  close() {
    this._listRequestId++;
    this.visible = false;
    this.customerId = 0;
    this.customerName = '';
    this.emails = [];
    this.editingId = null;
    this.newEmail = '';
    this.emailInputError = '';
  }

  /**
   * 从后端加载当前页的邮箱列表
   *
   * 调用 customerApi.emails 获取分页数据，更新 emails、total、
   * currentPage、perPage 状态。customerId 为 0 时直接返回。
   *
   * @returns Promise<void>
   */
  private async loadEmails() {
    if (!this.customerId) return;
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const response = await customerApi.emails(this.customerId, {
        page: this.currentPage,
        per_page: this.perPage,
      });
      if (requestId !== this._listRequestId) return;
      this.emails = (response as any).data || [];
      this.total = (response as any).pagination?.total || 0;
      this.currentPage = (response as any).pagination?.current_page || 1;
      this.perPage = (response as any).pagination?.per_page || 20;
    } catch (err: any) {
      if (requestId !== this._listRequestId) return;
      if (!isNetworkError(err.message)) toast.error(err.message);
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 处理分页切换
   *
   * @param page - 目标页码
   */
  private handlePageChange(page: number) {
    this.currentPage = page;
    this.loadEmails();
  }


  /**
   * 处理添加邮箱操作
   *
   * 执行三重校验：
   * 1. 非空检查
   * 2. 邮箱格式校验（正则匹配）
   * 3. 重复检查（与已有邮箱列表对比，不区分大小写）
   *
   * 校验通过后调用后端添加 API。如果当前客户无邮箱，新邮箱自动设为主邮箱（is_primary=1）。
   *
   * @returns Promise<void>
   */
  private async handleAdd() {
    const trimmed = this.newEmail.trim();
    if (!trimmed) return;
    // 邮箱格式校验：必须包含 @ 和 . ，且无空格
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      this.emailInputError = '邮箱格式不正确';
      return;
    }
    // 去重校验：与已有邮箱列表对比（不区分大小写）
    if (this.emails.some(e => e.email.toLowerCase() === trimmed.toLowerCase())) {
      this.emailInputError = '该邮箱已存在';
      return;
    }
    this.emailInputError = '';
    try {
      // 如果客户当前无邮箱，新添加的自动设为主邮箱（is_primary=1），否则为普通邮箱（0）
      await customerApi.addEmail(this.customerId, trimmed, this.emails.length === 0 ? 1 : 0);
      this.newEmail = '';
      await this.loadEmails();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /**
   * 设置指定邮箱为该客户的主邮箱
   *
   * @param id - 邮箱 ID
   * @returns Promise<void>
   */
  private async setPrimary(id: number) {
    try {
      await customerApi.setPrimaryEmail(this.customerId, id);
      await this.loadEmails();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /**
   * 处理删除邮箱操作
   *
   * 弹出确认弹窗，用户确认后调用后端删除 API 并刷新列表。
   *
   * @param id - 要删除的邮箱 ID
   * @returns Promise<void>
   */
  private async handleDelete(id: number) {
    if (!(await showConfirm('确定删除此邮箱地址？'))) return;
    try {
      await customerApi.deleteEmail(this.customerId, id);
      await this.loadEmails();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /**
   * 进入行内编辑模式
   *
   * @param e - 要编辑的邮箱对象
   */
  private startEdit(e: CustomerEmail) {
    this.editingId = e.id;
    this.editEmail = e.email;
  }

  /**
   * 保存编辑后的邮箱地址
   *
   * 调用后端更新 API，成功后退出编辑模式并刷新列表。
   *
   * @returns Promise<void>
   */
  private async saveEdit() {
    if (this.editingId == null || !this.editEmail.trim()) return;
    try {
      await customerApi.updateEmail(this.customerId, this.editingId, this.editEmail.trim());
      this.editingId = null;
      this.editEmail = '';
      await this.loadEmails();
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message);
    }
  }

  /**
   * 渲染客户邮箱管理模态框
   *
   * 当 visible 为 false 时返回空模板（不渲染 DOM）。
   *
   * 模态框结构：
   * 1. 遮罩层（点击空白处关闭）
   * 2. 模态框容器
   *    - 头部：标题（客户名称）+ 关闭按钮
   *    - 内容区：
   *      - 添加邮箱输入框（带格式校验提示）
   *      - 工具栏（总数 + 刷新）
   *      - 邮箱表格（地址、验证状态、主邮箱标记、更新时间、操作按钮）
   *    - 底部：分页组件
   *
   * @returns Lit 模板（visible 为 false 时返回空）
   */
  render() {
    if (!this.visible) return html``;

    return html`
      <!-- 遮罩层：点击空白处关闭模态框 -->
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.close(); }}>
        <!-- 模态框容器：阻止冒泡避免误关 -->
        <div class="bg-surface rounded-md shadow-xl border border-border flex flex-col overflow-hidden mx-4" style="width: 900px; max-width: 90vw; max-height: 85vh;" @click=${(e: Event) => e.stopPropagation()}>
          <!-- 头部：标题（含客户名称）+ 关闭按钮 -->
          <div class="flex justify-between items-center px-6 pt-5 pb-4 border-b border-border flex-shrink-0">
            <h2 class="m-0 font-semibold text-xl text-text">管理邮箱 - ${this.customerName}</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.close}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>

          <!-- 内容区：可滚动 -->
          <div class="overflow-y-auto flex-1 min-h-0 p-6">
            <!-- 添加邮箱输入区：输入框 + 添加按钮 -->
            <div class="mb-4">
              <div class="flex gap-2 items-start">
                <div class="flex-1">
                  <!-- 邮箱输入框：Enter 键添加，输入时清除错误提示 -->
                  <input
                    type="email"
                    placeholder="输入邮箱地址后按 Enter 添加..."
                    .value=${this.newEmail}
                    @input=${(e: Event) => { this.newEmail = (e.target as HTMLInputElement).value; this.emailInputError = ''; }}
                    @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); this.handleAdd(); } }}
                    class="w-full px-3.5 py-2.5 border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all ${this.emailInputError ? 'border-error' : 'border-border'}"
                  />
                  <!-- 格式/重复校验错误提示 -->
                  ${this.emailInputError ? html`<p class="mt-1 text-xs text-error">${this.emailInputError}</p>` : ''}
                </div>
                <button class="bg-primary hover:bg-primary-hover text-white px-4 py-1.5 rounded text-sm font-medium transition-colors cursor-pointer flex-shrink-0" @click=${this.handleAdd}>添加</button>
              </div>
            </div>

            <!-- 工具栏：邮箱总数 + 刷新按钮 -->
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-4">
                <span class="text-xs text-text-muted">共 ${this.total} 个邮箱地址</span>
                <button class="text-xs text-text-secondary hover:text-text transition-colors cursor-pointer bg-transparent border-none p-0" @click=${this.loadEmails}>刷新</button>
              </div>
            </div>

            <!-- 邮箱表格 -->
            <div class="overflow-x-auto relative">
              <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>
              <table class="w-full">
                <thead>
                  <tr class="border-b border-border">
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">邮箱地址</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">验证状态</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">主邮箱</th>
                    <th class="px-4 py-2.5 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">更新时间</th>
                    <th class="px-4 py-2.5 text-center text-xs font-medium text-text-secondary uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 空状态：无数据且非加载中 -->
                  ${!this.isLoading && this.emails.length === 0
                    ? html`<tr><td colspan="5" class="text-center py-12 text-text-muted">暂无邮箱地址</td></tr>`
                    : this.emails.map((e) => html`
                      <tr class="hover:bg-bg/50 transition-colors border-b border-border">
                        <!-- 编辑模式：整行变为输入框 + 保存/取消按钮 -->
                        ${this.editingId === e.id
                          ? html`
                              <td colspan="5" class="py-3">
                                <div class="flex gap-2 items-center">
                                  <!-- 编辑输入框：Enter 保存 -->
                                  <input type="email" class="flex-1 px-3.5 py-1.5 border border-border rounded text-sm bg-surface focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all" .value=${this.editEmail} @input=${(ev: Event) => (this.editEmail = (ev.target as HTMLInputElement).value)} @keydown=${(ev: KeyboardEvent) => { if (ev.key === 'Enter') this.saveEdit(); }} />
                                  <button class="bg-success hover:bg-success-hover text-white px-3 py-1.5 rounded text-xs font-medium transition-colors cursor-pointer" @click=${this.saveEdit}>保存</button>
                                  <button class="bg-bg hover:bg-border border border-border text-text-secondary px-3 py-1.5 rounded text-xs font-medium transition-colors cursor-pointer" @click=${() => (this.editingId = null)}>取消</button>
                                </div>
                              </td>
                            `
                          : html`
                              <!-- 正常显示模式 -->
                              <td class="px-4 py-2 text-sm font-medium text-text">${e.email || '-'}</td>
                              <!-- 验证状态徽章：valid=绿、invalid=红、risky=黄、pending=灰 -->
                              <td class="py-2 px-3 text-sm">
                                ${e.validation_status && e.validation_status !== 'pending' ? html`
                                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                    ${e.validation_status === 'valid' ? 'bg-success/10 text-success' :
                                      e.validation_status === 'invalid' ? 'bg-error/10 text-error' :
                                      e.validation_status === 'risky' ? 'bg-warning/10 text-warning' :
                                      'bg-border text-text-muted'}">
                                    ${e.validation_status === 'valid' ? '有效' :
                                      e.validation_status === 'invalid' ? '无效' :
                                      e.validation_status === 'risky' ? '风险' : '待验证'}
                                  </span>
                                ` : html`<span class="text-xs text-text-muted">待验证</span>`}
                              </td>
                              <!-- 主邮箱标记：勾选图标或横杠 -->
                              <td class="px-4 py-2 text-center">
                                ${e.is_primary ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" class="w-5 h-5 text-success mx-auto"><path d="M20 6 9 17l-5-5"/></svg>` : html`<span class="text-text-muted text-sm">-</span>`}
                              </td>
                              <!-- 更新时间：后端返回 Unix 时间戳（秒），转毫秒后格式化 -->
                              <td class="px-4 py-2 text-sm text-text-muted">${e.updated_at ? new Date(e.updated_at * 1000).toLocaleString('zh-CN') : '-'}</td>
                              <!-- 操作按钮：设为主邮箱 / 编辑 / 删除 -->
                              <td class="px-4 py-2 flex gap-1.5 flex-wrap">
                                <!-- 非主邮箱才显示"设为主"按钮 -->
                                ${!e.is_primary ? html`<button class="text-text-secondary hover:text-text text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.setPrimary(e.id)}>设为主</button>` : ''}
                                <button class="text-primary hover:text-primary-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.startEdit(e)}>编辑</button>
                                <button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.handleDelete(e.id)}>删除</button>
                              </td>
                            `}
                      </tr>
                    `)}
                </tbody>
              </table>
            </div>
          </div>

          <!-- 底部：分页组件 -->
          <crm-pagination
            .current=${this.currentPage}
            .perPage=${this.perPage}
            .total=${this.total}
            @page-change=${(e: CustomEvent) => this.handlePageChange(e.detail.page)}
          ></crm-pagination>
        </div>
      </div>
    `;
  }
}
